import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Cotizacion, CotizacionCreateRequest } from '../types/cotizacion.types';
import { CotizacionService } from '../services/cotizacionService';

interface CotizacionState {
  cotizaciones: Cotizacion[];
  selectedCotizacion: Cotizacion | null;
  loading: boolean;
  error: string | null;
  dialogOpen: boolean;

  fetchCotizaciones: () => Promise<void>;
  fetchCotizacionById: (id: number) => Promise<void>;
  saveCotizacion: (data: CotizacionCreateRequest) => Promise<void>;
  deleteCotizacion: (id: number) => Promise<void>;
  convertirAVenta: (id: number) => Promise<void>;
  setSelectedCotizacion: (cotizacion: Cotizacion | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  cotizaciones: [],
  selectedCotizacion: null,
  loading: false,
  error: null,
  dialogOpen: false,
};

export const useCotizacionStore = create<CotizacionState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchCotizaciones: async () => {
        set({ loading: true, error: null }, false, 'cotizacion/fetchAll/start');
        try {
          const cotizaciones = await CotizacionService.getAll();
          set({ cotizaciones, loading: false }, false, 'cotizacion/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar cotizaciones' }, false, 'cotizacion/fetchAll/error');
        }
      },

      fetchCotizacionById: async (id: number) => {
        set({ loading: true, error: null }, false, 'cotizacion/fetchById/start');
        try {
          const cotizacion = await CotizacionService.getById(id);
          set({ selectedCotizacion: cotizacion, loading: false }, false, 'cotizacion/fetchById/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar cotizacion' }, false, 'cotizacion/fetchById/error');
        }
      },

      saveCotizacion: async (data: CotizacionCreateRequest) => {
        set({ loading: true, error: null }, false, 'cotizacion/save/start');
        try {
          await CotizacionService.save(data);
          set({ loading: false, dialogOpen: false }, false, 'cotizacion/save/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al guardar cotizacion' }, false, 'cotizacion/save/error');
          throw error;
        }
      },

      deleteCotizacion: async (id: number) => {
        set({ loading: true, error: null }, false, 'cotizacion/delete/start');
        try {
          await CotizacionService.delete(id);
          set((state) => ({
            cotizaciones: state.cotizaciones.filter((c) => c.idCotizacion !== id),
            loading: false,
          }), false, 'cotizacion/delete/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al eliminar cotizacion' }, false, 'cotizacion/delete/error');
        }
      },

      convertirAVenta: async (id: number) => {
        set({ loading: true, error: null }, false, 'cotizacion/convertir/start');
        try {
          await CotizacionService.convertirAVenta(id);
          set({ loading: false }, false, 'cotizacion/convertir/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al convertir cotizacion' }, false, 'cotizacion/convertir/error');
          throw error;
        }
      },

      setSelectedCotizacion: (cotizacion) => set({ selectedCotizacion: cotizacion }, false, 'cotizacion/setSelected'),
      openDialog: () => set({ dialogOpen: true }, false, 'cotizacion/openDialog'),
      closeDialog: () => set({ dialogOpen: false, selectedCotizacion: null }, false, 'cotizacion/closeDialog'),
      clearError: () => set({ error: null }, false, 'cotizacion/clearError'),
      reset: () => set(initialState, false, 'cotizacion/reset'),
    }),
    { name: 'cotizacion-store' }
  )
);
